import React, { useState } from 'react';

import * as api from 'shared/services/reportingApi';
import { List, IListItemProps } from 'components/ui/Lists';
import { LabelledCheckbox } from 'components/ui/LabelledCheckbox';
import { IConfirmProps } from 'components/ui/ConfirmationBox';
import { TabPanel } from 'components/ui/TabPanel';
import PortalPage from 'components/portal/PortalPage';
import { LoginHistoryReportQuery } from 'shared/models/api/models';
import { ISelectedExportColumn, ExportGrid } from './ExportGrid'

import Chip from "@mui/material/Chip";
import { Paper, Tabs, Tab } from "@mui/material";
import { SupervisorAccountOutlined, PersonOutlined, NearMeOutlined, BarChartOutlined, MailOutline } from '@mui/icons-material';
import { ReactComponent as ReportingIcon } from 'assets/icons/reports-filled.svg';
import { ReactComponent as BuildCircle } from 'assets/icons/build-circle.svg';
import { ReactComponent as AddChart } from 'assets/icons/add-chart.svg';
import 'react-datepicker/dist/react-datepicker.css';
import 'components/ui/ConfirmationBox.css';
import 'components/portal/administration/administration.css';
import './Reporting.css';
import { blobDownload } from './blobDownload'


export const UserListItem = (props: IListItemProps<api.User>) =>
    <div className="list-item user-list-item" onClick={() => props.toggle(props.item)}>
        <LabelledCheckbox checkedValue={props.selected === true}><span className="name">{props.item.fullName}</span></LabelledCheckbox>
        <div>
            {props.item.city &&
                <span className="location"><NearMeOutlined /> {props.item.city}</span>
            }

            {props.item.supervisor.length > 0 &&
                <span className="supervisor"><SupervisorAccountOutlined />{props.item.supervisor}</span>
            }

            {!props.item.city && props.item.email.length > 0 &&
                <span className="email"><MailOutline />{props.item.email}</span>
            }

        </div>
    </div>;

interface IChipsProps<T> {
    values: T[],
    removeItem: (item: T) => void,
    prefix?: any
}

const UserChips = (props: IChipsProps<api.User>) =>
    <>
        {props.values &&
            props.values.map(usr =>
                <Chip
                    key={usr.id}
                    onDelete={() => { props.removeItem(usr); }}
                    label={<>
                        <b>{props.prefix ? props.prefix + ": " : ""}</b>{usr.fullName}
                    </>} />
            )
        }
    </>;


const LoginHistoryReport = () => {
    const [downloadState, setDownloadState] = useState("ready" as "ready" | "busy");
    const [historyResult, setHistoryResult] = useState(undefined as api.LoginHistoryReportResult | undefined);
    const [historyResultPaged, setHistoryResultPaged] = useState(undefined as api.LoginHistoryReportResult | undefined);

    const [exportState, setExportState] = useState("ready" as "ready" | "busy");
    const [stateVersion, setStateVersion] = useState(0);
    const [confirmProps, setConfirmProps] = useState(null as IConfirmProps | null);
    const [userFilterTab, setUserFilterTab] = useState(0);

    const [selectedUsers, setSelectedUsers] = useState([] as api.User[]);
    const [usersByNameFilter, setUsersByNameFilter] = useState("");
    const [usersByNameExpanded, setUsersByNameExpanded] = useState(false);
    const [usersByNameSummary, setUsersByNameSummary] = useState("");
    const [usersByName, setUsersByName] = useState([] as api.User[]);

    const [pageSize, setPageSize] = useState(100);
    const [pageNumber, setPageNumber] = useState(1);

    const forceStateChange = () => {
        setStateVersion(stateVersion + 1);
    };

    const historyColumns = [
        { seq: 1, definition: { title: "Id", name: "loginHistoryResultId", selected: false, unsortable: true } },
        { seq: 2, definition: { title: "Name", name: "fullname", selected: false, unsortable: true } },
        { seq: 3, definition: { title: "Email", name: "email", selected: false, unsortable: true } },
        { seq: 4, definition: { title: "Source", name: "source", selected: false, unsortable: true } },
        { seq: 5, definition: { title: "Date", name: "date", selected: false, unsortable: true } }
    ] as ISelectedExportColumn[];

    const selectItems = <T,>(
        srcItems: T[],
        destinationItems: T[],
        comparer: (a: T, b: T) => boolean,
        setter: (items: T[]) => void
    ) => {
        for (var i = 0; i < srcItems.length; i++) {
            if (destinationItems.findIndex(d => comparer(d, srcItems[i])) === -1) {
                destinationItems.push(srcItems[i]);
            }
        }

        setter(destinationItems);
        forceStateChange();
    };

    const getExportFilter = () => {
        let userIds = selectedUsers.map(user => user.id);

        return {
            userIds: userIds
        } as LoginHistoryReportQuery;
    };

    const viewResults = async () => {
        if (downloadState === "ready") {
            const filter = getExportFilter();

            if (filter) {
                setDownloadState("busy");

                api.getLoginHistoryResults(filter)
                    .then(result => {
                        setDownloadState("ready");

                        if (result) {
                            setHistoryResult(result);
                            setPagedResults(result, 1);
                        }
                    })
                    .catch(e => {
                        confirm("Error fetching data.", ["Close"], "Close");
                        setDownloadState("ready");
                    });
            }
        }
    };

    const setPagedResults = (result: api.LoginHistoryReportResult, page: number) => {
        setPageNumber(page);
        var items = [...result.list];
        var start = pageSize * (page - 1);
        var deleteCount = pageSize;
        items = items.splice(start, deleteCount);

        var paged = {
            totalResults: result.totalResults,
            list: items
        } as api.LoginHistoryReportResult;
        setHistoryResultPaged(paged);
    }

    const downloadExportResults = async () => {
        if (exportState === "ready") {
            const filter = getExportFilter();

            if (filter) {
                setExportState("busy");

                api.downloadLoginHistoryResults(filter)
                    .then(file => {
                        setExportState("ready");
                        blobDownload(file);
                    })
                    .catch(e => {
                        const message = (e instanceof RangeError) ? e.message : "Error fetching data. ";
                        confirm(message, ["Close"], "Close");
                        setExportState("ready");
                    });
            }
        }
    };

    const toggleItem = <T,>(
        item: T,
        arr: T[],
        comparerPredicate: (value: T) => boolean,
        setter: (value: T[]) => void
    ): boolean => {
        const idx = arr.findIndex(comparerPredicate);

        var result = false;

        if (idx === -1) {
            result = true;
            arr.push(item);
        } else {
            arr.splice(idx, 1);
        }

        setter(arr);
        forceStateChange();

        return result;
    };

    const toggleUser = (usr: api.User) => {
        toggleItem(usr, selectedUsers, u => { return u.id === usr.id }, setSelectedUsers);
    };

    const searchUsersByName = async (name: string) => {
        const result = await api.getUsersByName(name);

        if (result) {
            setUsersByName(result);
            setUsersByNameSummary(result.length + " search results for the search \"" + name + "\"");
        }

        return result;
    };

    const confirm = async (message: string, opts?: string[], defaultOption?: string, cancelOption?: string): Promise<string> => {
        return new Promise<string>((resolve) => {
            setConfirmProps({
                message: message,
                options: opts ?? ["OK", "Cancel"],
                defaultOption: defaultOption,
                setResult: (value) => {
                    setConfirmProps({} as IConfirmProps);
                    resolve(value);
                }
            });
        });
    };

    const View = () => {
        return (
            <div className="main-body-container reporting-module">

                <div className="section-1">
                    <div className="header">
                        <span className="section-1 icon"><BuildCircle /></span>
                        <span className="section-1 text">Build a report</span>
                    </div>
                </div>

                <div className="section-2">
                    <div className="header">
                        <span className="icon"><AddChart /></span>
                        <span className="text">Add data</span>
                    </div>
                </div>

                <div className="section-3">
                    <div className="header">
                        <span className="icon"><PersonOutlined /></span>
                        <span className="text">User</span>
                    </div>
                    <div className="divider" />
                </div>

                <div className="section-4">

                    <div className="body">
                        <Paper>
                            <Tabs value={userFilterTab} onChange={(e, v) => { setUserFilterTab(v); }}>
                                <Tab label="Find a user" />
                            </Tabs>

                            <TabPanel value={userFilterTab} index={0}>
                                <List<api.User>
                                    summary={usersByNameSummary}
                                    dataSource={usersByName}
                                    listItemType={UserListItem}
                                    filterText={usersByNameFilter}
                                    setFilterText={setUsersByNameFilter}
                                    expanded={usersByNameExpanded}
                                    setExpanded={setUsersByNameExpanded}
                                    buildKey={item => item.id}
                                    filterAsync={searchUsersByName}
                                    toggleSelected={toggleUser}
                                    selectItems={
                                        (filteredItems) => selectItems(
                                            filteredItems,
                                            selectedUsers,
                                            (a, b) => a.id === b.id,
                                            setSelectedUsers
                                        )
                                    }
                                    noResultsMessage="No users found"
                                    filterTextPlaceholder="Enter first name, last name or email address"
                                    selected={selectedUsers} />
                            </TabPanel>

                        </Paper>

                        <span>
                            <UserChips prefix="User" values={selectedUsers} removeItem={toggleUser} />
                        </span>
                    </div>
                </div>

                <div className="section-3">
                    <div className="body" style={{ textAlign: "center" }}>
                        <button className={"button-face export-state-" + downloadState} onClick={viewResults}>View results</button>
                        <button className={"button-face export-state-" + exportState} onClick={downloadExportResults}>Export to CSV</button>
                    </div>
                </div>

                {(downloadState === 'busy' || exportState === 'busy') &&
                    <div className="export-busy-container">
                        <div className="export-busy-message">We're fetching your data...</div>
                    </div>
                }

                {historyResult && historyResultPaged &&
                    <>
                        <div className="section-1 results">
                            <div className="header">
                                <span className="icon"><BarChartOutlined /></span>
                                <span className="text">Results</span>
                            </div>
                        </div>
                        <div className="section-2 results">
                            <div className="body">
                                <ExportGrid
                                    columns={historyColumns}
                                    results={historyResultPaged}
                                    pageNumber={pageNumber}
                                    pageSize={pageSize}
                                    setPageSize={setPageSize}
                                    state={downloadState}
                                    moveToPage={(n: number) => setPagedResults(historyResult, n)}
                                    sortOrder={[]}
                                />
                            </div>
                        </div>
                    </>
                }

            </div>
        );
    };

    return (
        <PortalPage module=""
            page="reporting"
            title={"Login History reporting"}
            route={"/reporting/login-history"}
            loadingState={exportState}
            icon={ReportingIcon}
            confirmationProps={confirmProps}>

            <View />
        </PortalPage>
    );
};

export default LoginHistoryReport;

